import React, { useState, useEffect } from 'react';
import { showcaseApi, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

const ICON_SUGGESTIONS = [
  { label: 'User Doctor / MD', value: 'fas fa-user-md' },
  { label: 'Headphones / Audio', value: 'fas fa-headphones' },
  { label: 'Users / Community', value: 'fas fa-users' },
  { label: 'Growth / Chart', value: 'fas fa-chart-line' },
  { label: 'Heart / Care', value: 'fas fa-heart' },
  { label: 'Brain / Mind', value: 'fas fa-brain' },
  { label: 'Shield / Confidential', value: 'fas fa-shield-heart' },
  { label: 'Seedling / Healing', value: 'fas fa-seedling' },
  { label: 'Sparkles / Inspiration', value: 'fas fa-sparkles' },
  { label: 'Comments / Discussion', value: 'fas fa-comments' },
];

function AdminShowcasePage() {
  const [slides, setSlides] = useState([]);
  const [settings, setSettings] = useState({
    sectionTitle: 'Explore PsychMitra',
    bgImage: 'assets/images/backgrounds/why-choose-two-bg.png',
    autoPlayInterval: 5500,
    isEnabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  // Modals state
  const [isSlideModalOpen, setIsSlideModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Active / editing item
  const [editingSlide, setEditingSlide] = useState(null);
  const [slideToDelete, setSlideToDelete] = useState(null);
  const [previewSlide, setPreviewSlide] = useState(null);

  // Slide form state
  const [formData, setFormData] = useState({
    tag: '',
    tagIcon: 'fas fa-heart',
    title: '',
    subtitle: '',
    description: '',
    btnText: 'Explore More',
    btnLink: '/services',
    image: '',
    alt: '',
    order: 0,
    isActive: true,
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch all slides and settings
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (statusFilter !== 'all') params.status = statusFilter;

      const res = await showcaseApi.adminGetSlides(params);
      if (res?.success) {
        setSlides(res.data || []);
        if (res.meta?.settings) {
          setSettings(res.meta.settings);
        }
      }
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.message || 'Failed to load showcase slides',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // Open modal for Create
  const handleOpenCreateModal = () => {
    setEditingSlide(null);
    setFormData({
      tag: 'Mental Health Care',
      tagIcon: 'fas fa-user-md',
      title: '',
      subtitle: '',
      description: '',
      btnText: 'Explore More',
      btnLink: '/services',
      image: 'assets/images/1/Mental Health.png',
      alt: '',
      order: slides.length + 1,
      isActive: true,
    });
    setIsSlideModalOpen(true);
  };

  // Open modal for Edit
  const handleOpenEditModal = (slide) => {
    setEditingSlide(slide);
    setFormData({
      tag: slide.tag || '',
      tagIcon: slide.tagIcon || 'fas fa-heart',
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      description: slide.description || '',
      btnText: slide.btnText || 'Explore More',
      btnLink: slide.btnLink || '/services',
      image: slide.image || '',
      alt: slide.alt || '',
      order: slide.order !== undefined ? slide.order : 0,
      isActive: slide.isActive !== undefined ? slide.isActive : true,
    });
    setIsSlideModalOpen(true);
  };

  // Open live preview modal
  const handleOpenPreview = (slide) => {
    setPreviewSlide(slide);
    setIsPreviewModalOpen(true);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const data = new FormData();
    data.append('file', file);

    setUploadingImage(true);
    try {
      const res = await showcaseApi.uploadImage(data);
      if (res?.fileUrl || res?.path || res?.url) {
        const uploadedPath = res.fileUrl || res.path || res.url;
        setFormData((prev) => ({ ...prev, image: uploadedPath }));
        setFeedback({ type: 'success', text: 'Image uploaded successfully!' });
      } else {
        setFeedback({ type: 'error', text: 'Upload succeeded but no image path returned.' });
      }
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Image upload failed' });
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle slide form submit (Create or Update)
  const handleSlideSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      setFeedback({ type: 'error', text: 'Please fill in the required title and description.' });
      return;
    }

    setSubmitting(true);
    try {
      if (editingSlide) {
        await showcaseApi.adminUpdateSlide(editingSlide._id, formData);
        setFeedback({ type: 'success', text: 'Showcase slide updated successfully!' });
      } else {
        await showcaseApi.adminCreateSlide(formData);
        setFeedback({ type: 'success', text: 'New showcase slide created successfully!' });
      }
      setIsSlideModalOpen(false);
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Failed to save showcase slide' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle slide delete
  const handleDeleteConfirm = async () => {
    if (!slideToDelete) return;
    setSubmitting(true);
    try {
      await showcaseApi.adminDeleteSlide(slideToDelete._id);
      setFeedback({ type: 'success', text: 'Showcase slide deleted successfully!' });
      setIsDeleteModalOpen(false);
      setSlideToDelete(null);
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Failed to delete slide' });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle active status
  const handleToggleStatus = async (slide) => {
    try {
      await showcaseApi.adminToggleStatus(slide._id);
      setSlides((prev) =>
        prev.map((s) => (s._id === slide._id ? { ...s, isActive: !s.isActive } : s))
      );
      setFeedback({
        type: 'success',
        text: `Slide "${slide.title}" status toggled successfully!`,
      });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Failed to toggle status' });
    }
  };

  // Move slide up/down order
  const handleMoveOrder = async (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= slides.length) return;

    const newSlides = [...slides];
    const temp = newSlides[index];
    newSlides[index] = newSlides[targetIndex];
    newSlides[targetIndex] = temp;

    const orders = newSlides.map((s, idx) => ({ id: s._id, order: idx + 1 }));

    // Optimistic update
    setSlides(newSlides.map((s, idx) => ({ ...s, order: idx + 1 })));

    try {
      await showcaseApi.adminReorderSlides(orders);
      setFeedback({ type: 'success', text: 'Slide order updated!' });
    } catch (err) {
      setFeedback({ type: 'error', text: 'Failed to update order on server.' });
      fetchData();
    }
  };

  // Handle settings update
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await showcaseApi.adminUpdateSettings(settings);
      setFeedback({ type: 'success', text: 'Showcase section settings updated successfully!' });
      setIsSettingsModalOpen(false);
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Failed to update settings' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reset to default
  const handleResetData = async () => {
    if (
      !window.confirm(
        'Are you sure you want to reset all showcase slides and settings to original defaults? This will erase custom slides.'
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await showcaseApi.adminResetData();
      setFeedback({ type: 'success', text: 'Showcase data reset to default successfully!' });
      fetchData();
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Failed to reset data' });
    } finally {
      setLoading(false);
    }
  };

  const activeCount = slides.filter((s) => s.isActive).length;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc', color: '#1e293b' }}>
      <DashboardSidebar userRole="admin" />

      <main className="sidebar-content-wrapper" style={{ flex: 1, padding: '32px 40px', minHeight: '100vh', width: 'calc(100% - 280px)', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Top Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '25px' }}>
          <div>
            <h1 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#1e293b', margin: '0 0 6px 0' }}>
              🌟 Homepage Showcase Slider
            </h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: '0.95rem' }}>
              Manage the 4 dynamic interactive showcase slides below the hero video section on the homepage.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleOpenCreateModal}
              style={{
                backgroundColor: '#5b9655',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                padding: '10px 18px',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(91, 150, 85, 0.25)',
              }}
            >
              <i className="fas fa-plus"></i> Add New Slide
            </button>

            <button
              type="button"
              onClick={() => setIsSettingsModalOpen(true)}
              style={{
                backgroundColor: '#ffffff',
                color: '#334155',
                border: '1.5px solid #cbd5e1',
                borderRadius: '12px',
                padding: '10px 16px',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <i className="fas fa-gear"></i> Section Settings
            </button>

            <button
              type="button"
              onClick={handleResetData}
              title="Reset to 4 default initial slides"
              style={{
                backgroundColor: '#fff1f2',
                color: '#e11d48',
                border: '1px solid #fecdd3',
                borderRadius: '12px',
                padding: '10px 14px',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <i className="fas fa-rotate-left"></i> Reset Defaults
            </button>
          </div>
        </div>

        {/* Feedback Alert Toast */}
        {feedback.text && (
          <div
            style={{
              position: 'fixed',
              top: '25px',
              right: '25px',
              zIndex: 999999,
              padding: '14px 22px',
              borderRadius: '12px',
              color: '#ffffff',
              backgroundColor: feedback.type === 'error' ? '#ef4444' : '#10b981',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
              fontWeight: 700,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              animation: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <i className={`fas ${feedback.type === 'error' ? 'fa-circle-exclamation' : 'fa-circle-check'}`}></i>
            <span>{feedback.text}</span>
            <button
              type="button"
              onClick={() => setFeedback({ type: '', text: '' })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ffffff', marginLeft: '8px', fontSize: '16px' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Stats Row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '18px',
            marginBottom: '25px',
          }}
        >
          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
            <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>Total Showcase Slides</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e293b' }}>{slides.length}</div>
          </div>

          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
            <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>Active on Homepage</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#16a34a' }}>{activeCount}</div>
          </div>

          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
            <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>Auto-play Interval</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#6366f1' }}>{(settings.autoPlayInterval || 5500) / 1000}s</div>
          </div>

          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
            <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>Section Status</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: settings.isEnabled ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: settings.isEnabled ? '#16a34a' : '#dc2626' }}></span>
              {settings.isEnabled ? 'Visible on Homepage' : 'Hidden'}
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div
          style={{
            background: '#ffffff',
            padding: '16px 20px',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '10px', flex: '1', maxWidth: '400px' }}>
            <div style={{ position: 'relative', width: '100%' }}>
              <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
              <input
                type="text"
                placeholder="Search slides by title, tag, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px 9px 36px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.9rem',
                }}
              />
            </div>
            <button type="submit" style={{ padding: '9px 16px', borderRadius: '10px', background: '#334155', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
              Search
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.88rem', color: '#64748b', fontWeight: 600 }}>Filter:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '0.88rem',
                backgroundColor: '#fff',
                cursor: 'pointer',
              }}
            >
              <option value="all">All Slides</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Slides Table */}
        <div style={{ background: '#ffffff', borderRadius: '18px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          {loading ? (
            <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <p style={{ margin: 0 }}>Loading showcase slides...</p>
            </div>
          ) : slides.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
              <i className="fas fa-layer-group" style={{ fontSize: '36px', color: '#cbd5e1', marginBottom: '12px' }}></i>
              <h3 style={{ fontSize: '1.2rem', color: '#334155', marginBottom: '6px' }}>No showcase slides found</h3>
              <p style={{ margin: '0 0 16px 0', fontSize: '0.9rem' }}>Create your first slide or reset to initial default slides.</p>
              <button
                type="button"
                onClick={handleOpenCreateModal}
                style={{ padding: '9px 18px', background: '#5b9655', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer' }}
              >
                + Add Slide
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 700 }}>
                    <th style={{ padding: '14px 16px', width: '80px' }}>Order</th>
                    <th style={{ padding: '14px 16px', width: '100px' }}>Image</th>
                    <th style={{ padding: '14px 16px', width: '220px' }}>Tag &amp; Icon</th>
                    <th style={{ padding: '14px 16px' }}>Title &amp; Subtitle</th>
                    <th style={{ padding: '14px 16px', width: '150px' }}>CTA Button</th>
                    <th style={{ padding: '14px 16px', width: '100px' }}>Status</th>
                    <th style={{ padding: '14px 16px', width: '180px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slides.map((slide, idx) => (
                    <tr
                      key={slide._id || idx}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {/* Order Column with Reorder Controls */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              background: '#eef2ff',
                              color: '#4f46e5',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {idx + 1}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <button
                              type="button"
                              disabled={idx === 0}
                              onClick={() => handleMoveOrder(idx, 'up')}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: idx === 0 ? 'not-allowed' : 'pointer',
                                color: idx === 0 ? '#cbd5e1' : '#64748b',
                                fontSize: '11px',
                                padding: '1px',
                              }}
                              title="Move Up"
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              disabled={idx === slides.length - 1}
                              onClick={() => handleMoveOrder(idx, 'down')}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: idx === slides.length - 1 ? 'not-allowed' : 'pointer',
                                color: idx === slides.length - 1 ? '#cbd5e1' : '#64748b',
                                fontSize: '11px',
                                padding: '1px',
                              }}
                              title="Move Down"
                            >
                              ▼
                            </button>
                          </div>
                        </div>
                      </td>

                      {/* Image Preview */}
                      <td style={{ padding: '14px 16px' }}>
                        <div
                          style={{
                            width: '70px',
                            height: '50px',
                            borderRadius: '8px',
                            background: '#f1f5f9',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #e2e8f0',
                          }}
                        >
                          <img
                            src={getMediaUrl(slide.image) || 'assets/images/1/Mental Health.png'}
                            alt={slide.alt || slide.title}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </div>
                      </td>

                      {/* Tag & Icon */}
                      <td style={{ padding: '14px 16px' }}>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: '#eef6ed',
                            color: '#5b9655',
                            padding: '4px 10px',
                            borderRadius: '16px',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                          }}
                        >
                          <i className={slide.tagIcon || 'fas fa-heart'}></i>
                          <span>{slide.tag}</span>
                        </div>
                      </td>

                      {/* Title & Subtitle */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: '2px' }}>{slide.title}</div>
                        {slide.subtitle && (
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{slide.subtitle}</div>
                        )}
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px', maxWidth: '380px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {slide.description}
                        </div>
                      </td>

                      {/* Button Details */}
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontWeight: 600, color: '#334155', display: 'block' }}>{slide.btnText || 'Explore'}</span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{slide.btnLink || '/'}</span>
                      </td>

                      {/* Active Status Switch */}
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(slide)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            border: 'none',
                            cursor: 'pointer',
                            backgroundColor: slide.isActive ? '#dcfce7' : '#f1f5f9',
                            color: slide.isActive ? '#15803d' : '#64748b',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: slide.isActive ? '#15803d' : '#94a3b8',
                            }}
                          ></span>
                          {slide.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>

                      {/* Action Buttons */}
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenPreview(slide)}
                            title="Preview Slide"
                            style={{
                              border: '1px solid #cbd5e1',
                              background: '#fff',
                              color: '#475569',
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <i className="fas fa-eye" style={{ fontSize: '13px' }}></i>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(slide)}
                            title="Edit Slide"
                            style={{
                              border: '1px solid #cbd5e1',
                              background: '#fff',
                              color: '#2563eb',
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <i className="fas fa-pen-to-square" style={{ fontSize: '13px' }}></i>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setSlideToDelete(slide);
                              setIsDeleteModalOpen(true);
                            }}
                            title="Delete Slide"
                            style={{
                              border: '1px solid #fecdd3',
                              background: '#fff1f2',
                              color: '#e11d48',
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <i className="fas fa-trash" style={{ fontSize: '13px' }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ==================== CREATE / EDIT MODAL ==================== */}
        {isSlideModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px',
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '680px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                padding: '30px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
                <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>
                  {editingSlide ? '✏️ Edit Showcase Slide' : '➕ Create New Showcase Slide'}
                </h2>
                <button
                  type="button"
                  onClick={() => setIsSlideModalOpen(false)}
                  style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSlideSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* Tag Name */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                      Slide Tag / Category *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Guided Audio Care"
                      value={formData.tag}
                      onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                    />
                  </div>

                  {/* Tag Icon */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                      FontAwesome Icon Class
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        placeholder="e.g. fas fa-headphones"
                        value={formData.tagIcon}
                        onChange={(e) => setFormData({ ...formData, tagIcon: e.target.value })}
                        style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                      />
                      <div
                        style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '10px',
                          background: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#5b9655',
                          fontSize: '18px',
                          flexShrink: 0,
                          border: '1px solid #cbd5e1',
                        }}
                      >
                        <i className={formData.tagIcon || 'fas fa-heart'}></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Icon Quick Picks */}
                <div style={{ marginBottom: '18px' }}>
                  <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block', marginBottom: '6px' }}>
                    Quick pick icon:
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {ICON_SUGGESTIONS.map((icon) => (
                      <button
                        key={icon.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, tagIcon: icon.value })}
                        style={{
                          background: formData.tagIcon === icon.value ? '#eef6ed' : '#f8fafc',
                          color: formData.tagIcon === icon.value ? '#5b9655' : '#475569',
                          border: `1px solid ${formData.tagIcon === icon.value ? '#5b9655' : '#e2e8f0'}`,
                          borderRadius: '16px',
                          padding: '4px 10px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <i className={icon.value} style={{ marginRight: '4px' }}></i>
                        {icon.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                    Main Headline Title *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Calm Your Mind Through Guided Audios"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                  />
                </div>

                {/* Subtitle */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                    Subtitle (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Available in 2 languages and voices"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                  />
                </div>

                {/* Description */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                    Description *
                  </label>
                  <textarea
                    rows={3}
                    required
                    placeholder="Provide a clear, soothing, and compelling overview..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem', resize: 'vertical' }}
                  />
                </div>

                {/* CTA Button Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                      Button Text
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Listen to Audios"
                      value={formData.btnText}
                      onChange={(e) => setFormData({ ...formData, btnText: e.target.value })}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                      Button Target Link
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. /self-help or https://..."
                      value={formData.btnLink}
                      onChange={(e) => setFormData({ ...formData, btnLink: e.target.value })}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                    />
                  </div>
                </div>

                {/* Image Upload & URL */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                    Illustration / Slide Image
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div
                      style={{
                        width: '80px',
                        height: '60px',
                        borderRadius: '10px',
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      {formData.image ? (
                        <img src={getMediaUrl(formData.image)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <i className="fas fa-image" style={{ color: '#94a3b8' }}></i>
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Image URL or assets/images/..."
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem', marginBottom: '6px' }}
                      />
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>
                        <i className="fas fa-cloud-arrow-up"></i>
                        <span>{uploadingImage ? 'Uploading image...' : 'Upload Image File'}</span>
                        <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Order & Active Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#f8fafc', borderRadius: '12px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginRight: '10px' }}>
                      Sort Order:
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                      style={{ width: '70px', padding: '6px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ width: '18px', height: '18px', accentColor: '#5b9655' }}
                    />
                    <span>Active on Homepage</span>
                  </label>
                </div>

                {/* Submit Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setIsSlideModalOpen(false)}
                    style={{ padding: '10px 18px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{ padding: '10px 22px', borderRadius: '10px', border: 'none', background: '#5b9655', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {submitting ? 'Saving...' : editingSlide ? 'Update Slide' : 'Create Slide'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ==================== SECTION SETTINGS MODAL ==================== */}
        {isSettingsModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px',
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '540px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                padding: '30px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>
                  ⚙️ Showcase Section Settings
                </h2>
                <button
                  type="button"
                  onClick={() => setIsSettingsModalOpen(false)}
                  style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSettingsSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                    Section Title / Meta
                  </label>
                  <input
                    type="text"
                    value={settings.sectionTitle || ''}
                    onChange={(e) => setSettings({ ...settings, sectionTitle: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                    Background Pattern Image Path
                  </label>
                  <input
                    type="text"
                    value={settings.bgImage || ''}
                    onChange={(e) => setSettings({ ...settings, bgImage: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#334155', marginBottom: '6px' }}>
                    Auto-slide Transition Time (ms)
                  </label>
                  <input
                    type="number"
                    min={2000}
                    step={500}
                    value={settings.autoPlayInterval || 5500}
                    onChange={(e) => setSettings({ ...settings, autoPlayInterval: Number(e.target.value) })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                  />
                  <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Example: 5500 = 5.5 seconds per slide rotation.
                  </small>
                </div>

                <div style={{ padding: '14px 18px', background: '#f8fafc', borderRadius: '12px', marginBottom: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 700, color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={settings.isEnabled}
                      onChange={(e) => setSettings({ ...settings, isEnabled: e.target.checked })}
                      style={{ width: '18px', height: '18px', accentColor: '#5b9655' }}
                    />
                    <span>Enable / Display this section on Homepage</span>
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setIsSettingsModalOpen(false)}
                    style={{ padding: '10px 18px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{ padding: '10px 22px', borderRadius: '10px', border: 'none', background: '#334155', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {submitting ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ==================== LIVE PREVIEW MODAL ==================== */}
        {isPreviewModalOpen && previewSlide && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px',
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '860px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
                padding: '36px',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#5b9655', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  ✨ Live Slide Card Preview
                </span>
                <button
                  type="button"
                  onClick={() => setIsPreviewModalOpen(false)}
                  style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              {/* Preview Card */}
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '24px',
                  border: '1px solid #e9eee8',
                  padding: '30px',
                  display: 'grid',
                  gridTemplateColumns: '40% 60%',
                  gap: '24px',
                  alignItems: 'center',
                  boxShadow: '0 16px 40px rgba(28, 48, 30, 0.08)',
                }}
              >
                <div
                  style={{
                    background: 'linear-gradient(145deg, #f0f6ee 0%, #e3ede0 100%)',
                    borderRadius: '18px',
                    height: '260px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                  }}
                >
                  <img
                    src={getMediaUrl(previewSlide.image) || 'assets/images/1/Mental Health.png'}
                    alt={previewSlide.alt || previewSlide.title}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>

                <div>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#eef6ed',
                      color: '#5b9655',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      marginBottom: '12px',
                    }}
                  >
                    <i className={previewSlide.tagIcon || 'fas fa-heart'}></i>
                    <span>{previewSlide.tag}</span>
                  </div>

                  <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1a251e', margin: '0 0 6px 0' }}>
                    {previewSlide.title}
                  </h3>

                  {previewSlide.subtitle && (
                    <h5 style={{ fontSize: '1rem', fontWeight: 600, color: '#4a6350', margin: '0 0 12px 0' }}>
                      {previewSlide.subtitle}
                    </h5>
                  )}

                  <p style={{ fontSize: '0.9rem', color: '#555e56', lineHeight: 1.6, margin: '0 0 20px 0' }}>
                    {previewSlide.description}
                  </p>

                  <a
                    href={previewSlide.btnLink || '/'}
                    className="thm-btn"
                    style={{
                      display: 'inline-block',
                      padding: '10px 22px',
                      borderRadius: '30px',
                      background: '#5b9655',
                      color: '#fff',
                      fontWeight: 700,
                      textDecoration: 'none',
                    }}
                  >
                    {previewSlide.btnText || 'Explore'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== DELETE CONFIRM MODAL ==================== */}
        {isDeleteModalOpen && slideToDelete && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px',
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '440px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                padding: '28px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  margin: '0 auto 16px',
                }}
              >
                <i className="fas fa-trash"></i>
              </div>

              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>
                Delete Showcase Slide?
              </h3>
              <p style={{ fontSize: '0.9rem', color: '#64748b', margin: '0 0 20px 0' }}>
                Are you sure you want to delete "<strong>{slideToDelete.title}</strong>"? This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  style={{ padding: '10px 18px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={submitting}
                  style={{ padding: '10px 22px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                >
                  {submitting ? 'Deleting...' : 'Yes, Delete Slide'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminShowcasePage;
